import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { fetchArchdeaconryBySlug } from '@/services/api';

interface Clergy {
    id: number;
    name: string;
    title: string;
    slug: string;
    photo?: string;
}

interface Parish {
    id: number;
    name: string;
    slug: string;
    location: string;
}

interface Archdeaconry {
    id: number;
    name: string;
    slug: string;
    parishes: Parish[];
    priests: Clergy[];
    description: string;
    current_archdeacon?: Clergy;
    current_supervisor?: Clergy;
}

const getInitials = (name: string) =>
    name
        ?.split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase();

const ArchdeaconryProfile = () => {
    const { slug } = useParams<{ slug: string }>();
    const { toast } = useToast();
    const [archdeaconry, setArchdeaconry] = useState<Archdeaconry | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadArchdeaconry = async () => {
            try {
                const response = await fetchArchdeaconryBySlug(slug!);
                setArchdeaconry(response.data);
            } catch (error) {
                console.error(error);
                toast({
                    title: 'Error',
                    description: 'Failed to fetch archdeaconry details.',
                    variant: 'destructive',
                });
            } finally {
                setLoading(false);
            }
        };

        loadArchdeaconry();
    }, [slug, toast]);

    if (loading) {
        return (
            <div className="p-10 text-center text-gray-500 text-lg animate-pulse">
                Loading archdeaconry information...
            </div>
        );
    }

    if (!archdeaconry) {
        return (
            <div className="p-10 text-center text-red-500 text-lg">
                Archdeaconry not found.
            </div>
        );
    }

    const leader = archdeaconry.current_archdeacon || archdeaconry.current_supervisor;
    const leaderRole = archdeaconry.current_archdeacon ? 'Archdeacon' : 'Supervisor';

    return (
        <div className="max-w-4xl mx-auto px-4 py-10">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
                {archdeaconry.name}
            </h1>

            {/* Leader (Archdeacon or Supervisor) */}
            {leader && (
                <Card className="mb-8 border shadow-sm rounded-2xl">
                    <div className="flex items-center p-5 gap-4">
                        {leader.photo ? (
                            <img
                                src={leader.photo}
                                alt={leader.name}
                                className="w-16 h-16 rounded-full object-cover"
                            />
                        ) : (
                            <div className="w-16 h-16 rounded-full bg-blue-700 text-white flex items-center justify-center font-bold uppercase">
                                {getInitials(leader.name)}
                            </div>
                        )}
                        <div>
                            <p className="text-sm text-gray-500">{leaderRole}</p>
                            <h2 className="text-lg font-semibold text-blue-900">
                                <Link to={`/clergy/${leader.slug}`} className="hover:underline">
                                    {leader.title} {leader.name}
                                </Link>
                            </h2>
                        </div>
                    </div>
                </Card>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    {/* Parishes list */}
                    <div className="mb-8">
                        <h3 className="text-md font-semibold text-gray-700 mb-2">
                            Churches in this Archdeaconry
                        </h3>
                        {archdeaconry.parishes.length === 0 ? (
                            <p className="text-sm text-gray-500">No parishes found.</p>
                        ) : (
                            <ul className="list-disc pl-4 space-y-1 text-sm text-blue-700">
                                {archdeaconry.parishes.map((parish) => (
                                    <li key={parish.id}>
                                        <Link to={`/parishes/${parish.slug}`} className="hover:underline">
                                            {parish.name}, {parish.location}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <Separator className="my-6" />

                    {/* Clergy list */}
                    <div>
                        <h3 className="text-md font-semibold text-gray-700 mb-2">
                            Clergy Serving Here
                        </h3>
                        {archdeaconry.priests.length === 0 ? (
                            <p className="text-sm text-gray-500">No clergy listed.</p>
                        ) : (
                            <ul className="list-disc pl-4 space-y-1 text-sm text-blue-700">
                                {archdeaconry.priests.map((clergy) => (
                                    <li key={clergy.id}>
                                        <Link to={`/clergy/${clergy.slug}`} className="hover:underline">
                                            {clergy.title} {clergy.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
                <div>
                    {archdeaconry.description && (
                        <p className="text-gray-500 mt-2 whitespace-pre-line">
                            {archdeaconry.description}
                        </p>
                    )}
                </div>
            </div>

        </div>
    );
};

export default ArchdeaconryProfile;
